/**
 * @fileoverview Pure logic for a firm's custom sending domains on SES (G6,
 * MOR-2322): the identity's tags, the status mapped from `GetEmailIdentity`,
 * the DNS records the firm publishes, the apex-MX recommendation and the
 * mailbox-provisioning precedence.
 *
 * No `"use node"` and no AWS call: `domains.ts` (Node) reads the identity and
 * hands its output here, and `integrations/inkbox/internal.ts`'s
 * `provisionSesMailbox` (a mutation, which cannot call AWS) reads the status
 * the actions cached on the claim row. Only a type is imported from the SDK,
 * so this module bundles for Convex's default runtime.
 */

import type { GetEmailIdentityResponse } from "@aws-sdk/client-sesv2";
import type { Doc } from "../../_generated/dataModel";

/**
 * The region the platform's SES lives in, which every record below and the
 * SDK client are pinned to (AU data residency): the inbound and MAIL FROM
 * hosts differ per region, so one constant keeps them from disagreeing.
 */
export const SES_REGION = "ap-southeast-2";

/**
 * The tag keys written on every identity the app creates. MIRRORED by
 * `infra/core-infra/infra/instanceUser.policy.ts`, whose IAM conditions match
 * on them; `infra/core-infra/infra/instanceUser.policy.test.ts` reads this
 * file and fails if they drift.
 */
export const SENDING_DOMAIN_ORG_TAG = "Org";
export const SENDING_DOMAIN_INSTANCE_TAG = "Instance";

/** `10 feedback-smtp.<region>.amazonses.com`: where bounces to the MAIL FROM domain go. */
export const MAIL_FROM_MX_HOST = `feedback-smtp.${SES_REGION}.amazonses.com`;
/** `10 inbound-smtp.<region>.amazonaws.com`: SES's inbound endpoint, for mail to the firm's mailboxes. */
export const INBOUND_MX_HOST = `inbound-smtp.${SES_REGION}.amazonaws.com`;
export const MX_PRIORITY = 10;
export const MAIL_FROM_SPF_VALUE = "v=spf1 include:amazonses.com ~all";
/** A monitor-only policy: published as a recommendation, never verified. */
export const DMARC_VALUE = "v=DMARC1; p=none;";

/** The state the actions cache on the claim row, from the schema. */
export type SendingDomainStatus = NonNullable<
  Doc<"orgSendingDomains">["status"]
>;

/** The slice of `GetEmailIdentity`'s output this module reads. */
export type SesIdentity = Pick<
  GetEmailIdentityResponse,
  "DkimAttributes" | "MailFromAttributes" | "Tags"
>;

/**
 * Domain verification as the UI and `provisionSesMailbox` see it: `verified`
 * only when Easy DKIM and the custom MAIL FROM domain both verified, `failed`
 * or `temporary_failure` when either reports it, `pending` otherwise (a
 * missing attribute is not yet verified).
 */
export function sendingDomainStatus(
  identity: SesIdentity,
): SendingDomainStatus {
  const dkim = identity.DkimAttributes?.Status;
  const mailFrom = identity.MailFromAttributes?.MailFromDomainStatus;
  if (dkim === "FAILED" || mailFrom === "FAILED") return "failed";
  if (dkim === "TEMPORARY_FAILURE" || mailFrom === "TEMPORARY_FAILURE") {
    return "temporary_failure";
  }
  return dkim === "SUCCESS" && mailFrom === "SUCCESS" ? "verified" : "pending";
}

/** The custom MAIL FROM domain, a subdomain of the identity: `bounce.<domain>`. */
export const mailFromDomain = (domain: string): string => `bounce.${domain}`;

/** The tags every identity is created with. */
export function identityTags(
  orgId: string,
  instanceSlug: string,
): { Key: string; Value: string }[] {
  return [
    { Key: SENDING_DOMAIN_ORG_TAG, Value: orgId },
    { Key: SENDING_DOMAIN_INSTANCE_TAG, Value: instanceSlug },
  ];
}

/** Whether the identity carries this org's tag, the filter every read goes through. */
export function hasOrgTag(identity: SesIdentity, orgId: string): boolean {
  return (identity.Tags ?? []).some(
    (tag) => tag.Key === SENDING_DOMAIN_ORG_TAG && tag.Value === orgId,
  );
}

/** One record to publish at the firm's registrar, in the shape the Inkbox domain detail already used. */
export interface DnsRecord {
  type: "CNAME" | "MX" | "TXT";
  host: string;
  value: string;
  priority?: number;
  purpose: string;
  /** `valid`, `pending` or `invalid`; absent for records SES cannot check (inbound MX, DMARC). */
  status?: string;
}

const RECORD_STATUS: Record<string, string> = {
  SUCCESS: "valid",
  FAILED: "invalid",
  PENDING: "pending",
  TEMPORARY_FAILURE: "pending",
  NOT_STARTED: "pending",
};

const recordStatus = (status: string | undefined): string =>
  (status !== undefined ? RECORD_STATUS[status] : undefined) ?? "pending";

/**
 * Every record the firm publishes for one domain, from `GetEmailIdentity`:
 * the three Easy DKIM CNAMEs (`<token>._domainkey.<domain>` pointing at
 * `<token>.dkim.amazonses.com`), the MAIL FROM MX and its SPF TXT on
 * `bounce.<domain>`, the inbound MX on the domain and a DMARC TXT
 * recommendation. DKIM and MAIL FROM carry SES's verdict; the other two
 * cannot be checked from SES.
 */
export function buildDomainRecords(
  domain: string,
  identity: SesIdentity,
): DnsRecord[] {
  const dkimStatus = recordStatus(identity.DkimAttributes?.Status);
  const mailFromStatus = recordStatus(
    identity.MailFromAttributes?.MailFromDomainStatus,
  );
  const mailFrom = mailFromDomain(domain);
  return [
    ...(identity.DkimAttributes?.Tokens ?? []).map((token): DnsRecord => ({
      type: "CNAME",
      host: `${token}._domainkey.${domain}`,
      value: `${token}.dkim.amazonses.com`,
      purpose: "dkim",
      status: dkimStatus,
    })),
    {
      type: "MX",
      host: mailFrom,
      value: MAIL_FROM_MX_HOST,
      priority: MX_PRIORITY,
      purpose: "mail_from",
      status: mailFromStatus,
    },
    {
      type: "TXT",
      host: mailFrom,
      value: MAIL_FROM_SPF_VALUE,
      purpose: "spf",
      status: mailFromStatus,
    },
    {
      type: "MX",
      host: domain,
      value: INBOUND_MX_HOST,
      priority: MX_PRIORITY,
      purpose: "inbound",
    },
    {
      type: "TXT",
      host: `_dmarc.${domain}`,
      value: DMARC_VALUE,
      purpose: "dmarc_recommended",
    },
  ];
}

/** A domain as `listSendingDomains` and the detail actions report it (the Inkbox shape the UI already renders). */
export interface SendingDomainDetail {
  id: string;
  domain: string;
  status: string;
  isDefault: boolean;
  verifiedAt: number | null;
  dnsRecords: DnsRecord[];
}

/**
 * The detail for one domain. The id is the domain name itself (the SES
 * identity name), and SES reports no verification time, so `verifiedAt` is
 * always null.
 */
export function toSendingDomainDetail(
  domain: string,
  identity: SesIdentity,
  isDefault: boolean,
): SendingDomainDetail {
  return {
    id: domain,
    domain,
    status: sendingDomainStatus(identity),
    isDefault,
    verifiedAt: null,
    dnsRecords: buildDomainRecords(domain, identity),
  };
}

/**
 * The subdomain to recommend when the domain itself already receives mail:
 * `mail.<domain>`, or `send.<domain>` when the firm already chose `mail.`.
 */
export const suggestSubdomain = (domain: string): string =>
  `${domain.startsWith("mail.") ? "send" : "mail"}.${domain}`;

/**
 * The MX exchanges at the domain that belong to someone else: a null MX
 * (RFC 7505) and SES's own inbound host, which the firm publishes for us,
 * are not another provider, so the recommendation clears once the records
 * below are in place.
 */
export function foreignMxHosts(exchanges: readonly string[]): string[] {
  return exchanges
    .map((exchange) => exchange.trim().toLowerCase().replace(/\.$/, ""))
    .filter((host) => host !== "" && host !== INBOUND_MX_HOST);
}

/** The `apex_mx_warning` text: the domain already receives mail, so use a subdomain. */
export const apexMxMessage = (
  domain: string,
  hosts: readonly string[],
): string =>
  `${domain} already receives email (MX: ${hosts.join(", ")}). Use a subdomain such as ${suggestSubdomain(domain)} so your existing email keeps working, or continue to replace that provider.`;

/**
 * The domain new `ses` mailboxes are provisioned on: the org's default
 * claim, but only once SES verified it. Anything else (no default, a default
 * still propagating, an Inkbox-era claim, which carries no status) falls back
 * to the instance's own receiving domain, chosen by the caller.
 */
export function pickVerifiedDefaultDomain(
  claims: readonly Pick<
    Doc<"orgSendingDomains">,
    "domain" | "isDefault" | "status"
  >[],
): string | null {
  const claim = claims.find((c) => c.isDefault && c.status === "verified");
  return claim ? claim.domain.trim().toLowerCase() : null;
}

/**
 * Whether new `ses` mailboxes are provisioned on the org's verified default
 * domain (`pickVerifiedDefaultDomain`). OFF: a verified identity can send
 * (once the send grant allows its domain) but cannot yet RECEIVE, because
 * inbound needs MX routing and an SES receipt rule that lists the domain, and
 * that list is a separate morgan-bfmr deploy (`LEGACY_MAIL_CUSTOM_DOMAINS`).
 * An address on such a domain would be unreachable, so every mailbox stays on
 * `LEGACY_MAIL_DOMAIN`. Turn it on only when BOTH land: the bfmr receipt-rule
 * list, and the send-grant counterpart (the instance user's `ses:FromAddress`
 * pins sends to `mail.<slug>.<zone>`; MOR-2322 review thread, G3 MOR-2319).
 * Read by `provisionSesMailbox` and `getMailProviderInfo`, so the preview
 * shows the domain provisioning will use.
 */
export const CUSTOM_DOMAIN_PROVISIONING_ENABLED = false as boolean;

/** Shown by `restoreSendingDomain` on `ses`, where a removal is immediate. */
export const SES_RESTORE_UNAVAILABLE =
  "A removed domain cannot be restored on this deployment. Add the domain again to get fresh DNS records.";
